import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import './ResetPassword.css';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setStatus('error');
      setMessage('No reset token provided. Please request a new password reset link.');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setStatus('error');
      setMessage('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters long.');
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setStatus('error');
      setMessage('Password must contain at least one uppercase letter, one lowercase letter, and one number.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        new_password: password
      });

      setStatus('success');
      setMessage(response.data.message || 'Password reset successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password reset successfully. Please log in with your new password.' }
        });
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.detail ||
        'Failed to reset password. The link may be invalid or expired.'
      );
    }
  };

  if (!token && status === 'error') {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="verify-icon error">✗</div>
          <h2>Invalid Reset Link</h2>
          <p>{message}</p>
          <Link to="/forgot-password" className="btn-primary">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <h2>Reset Your Password</h2>
        <p className="subtitle">
          Enter your new password below. Make sure it's strong and secure.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={status === 'loading'}
              required
            />
            <small className="hint">
              At least 8 characters with uppercase, lowercase, and a number
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={status === 'loading'}
              required
            />
          </div>

          {message && (
            <div className={`message ${status === 'error' ? 'error' : 'success'}`}>
              {message}
            </div>
          )}

          {status === 'success' && (
            <p className="redirect-text">Redirecting to login page...</p>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={status === 'loading' || status === 'success'}
          >
            {status === 'loading' ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="links">
          <Link to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
}
